import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiCheck, FiCheckCircle, FiX, FiPackage } from 'react-icons/fi'; // Added FiPackage for low stock
import { useNotifications } from '../../hooks/useNotifications'; // Changed to generic useNotifications

const NotificationsDropdown = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, refetch } = useNotifications({
    pollInterval: 30000 // Poll every 30 seconds
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Navigate based on resource_type
    setIsOpen(false);
    switch (notification.resource_type) {
      case 'task':
        if (notification.resource_id) {
          navigate(`/tasks/${notification.resource_id}`);
        }
        break;
      case 'product_low_stock':
        if (notification.resource_id) {
          navigate(`/products/${notification.resource_id}`);
        }
        break;
      // Add more cases for other notification types as needed
      default:
        // Default navigation or do nothing if no specific route
        // For now, let's navigate to a generic notifications page if it exists
        // navigate('/notifications'); 
        console.log('No specific navigation for this notification type:', notification.resource_type);
        break;
    }
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'product_low_stock':
        return FiPackage;
      case 'task_assigned':
      case 'task_reassigned':
      case 'task_status_changed':
      case 'task_due_soon':
      case 'task_overdue':
      case 'task_completed':
        return FiCheck; // Using a generic task icon for now
      default:
        return FiBell;
    }
  };

  const getNotificationColor = (type) => {
    const colors = {
      task_assigned: 'text-blue-600 bg-blue-50',
      task_reassigned: 'text-purple-600 bg-purple-50',
      task_status_changed: 'text-accent-gold bg-yellow-50',
      task_due_soon: 'text-orange-600 bg-orange-50',
      task_overdue: 'text-red-600 bg-red-50',
      task_completed: 'text-green-600 bg-green-50',
      product_low_stock: 'text-red-600 bg-red-50', // Red for low stock
    };
    return colors[type] || 'text-gray-600 bg-gray-50';
  };

  const formatTime = (datetime) => {
    if (!datetime) return '';
    const d = new Date(datetime);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
      >
        <FiBell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 md:w-96 max-w-[400px] bg-white rounded-lg shadow-xl border border-gray-200 z-50 flex flex-col">
          {/* Header */}
          <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-semibold text-primary-black">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-accent-gold hover:text-accent-goldDark font-medium flex items-center gap-1"
                >
                  <FiCheckCircle className="h-3 w-3" />
                  <span className="hidden sm:inline">Mark all read</span>
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Notification List - Limited to 3 */}
          <div className="flex-1 overflow-y-auto max-h-[300px]">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <svg
                  className="animate-spin h-6 w-6 text-accent-gold"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <FiBell className="h-10 w-10 text-gray-300 mb-2" />
                <p className="text-sm text-gray-500 text-center">
                  No notifications yet
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  You'll see updates here
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.slice(0, 3).map((notification) => {
                  const Icon = getNotificationIcon(notification.notification_type);
                  const colorClass = getNotificationColor(notification.notification_type);

                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                        !notification.is_read ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2 sm:gap-3">
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${colorClass}`}>
                          <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs sm:text-sm line-clamp-2 ${!notification.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                            {notification.message}
                          </p>
                          <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                            {formatTime(notification.created_at)}
                          </p>
                        </div>

                        {/* Unread indicator */}
                        {!notification.is_read && (
                          <div className="flex-shrink-0">
                            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer - Show if there are notifications */}
          {notifications.length > 0 && (
            <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  navigate('/notifications');
                  setIsOpen(false);
                }}
                className="text-xs sm:text-sm text-accent-gold hover:text-accent-goldDark font-medium w-full text-center"
              >
                View All Notifications {notifications.length > 3 && `(${notifications.length})`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsDropdown;
